import { useMemo } from 'react'
import { CalendarCheck2, Newspaper } from 'lucide-react'
import type { Page } from '../../api/types'

interface FrontPageReadinessWidgetProps {
  livePage: Page | undefined
  scheduledPage: Page | undefined
  liveLoading: boolean
  scheduledLoading: boolean
  embedded?: boolean
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function pageStatus(page: Page | undefined, loading: boolean, label: string) {
  if (loading) return `${label} loading`
  if (!page) return `No ${label.toLowerCase()} page`
  const blockCount = page.blocks?.length ?? 0
  const hasSchedule = Boolean(page.scheduledFor)
  return `${blockCount} block${blockCount !== 1 ? 's' : ''}${hasSchedule ? ` · ${fmtDate(page.scheduledFor!)}` : ''}`
}

export function FrontPageReadinessWidget({ livePage, scheduledPage, liveLoading, scheduledLoading, embedded = false }: FrontPageReadinessWidgetProps) {
  const readiness = useMemo(() => {
    const liveBlocks = livePage?.blocks?.length ?? 0
    const scheduledBlocks = scheduledPage?.blocks?.length ?? 0
    return {
      liveBlocks,
      scheduledBlocks,
      liveReady: Boolean(livePage && liveBlocks > 0),
      scheduledReady: Boolean(scheduledPage && scheduledBlocks > 0),
    }
  }, [livePage, scheduledPage])

  const body = (
    <div className="space-y-2 px-4 py-4">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-fill/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <CalendarCheck2 size={14} aria-hidden="true" className="text-fg-secondary" />
          <p className="text-sm font-semibold text-fg">Front Page Readiness</p>
        </div>
        <span className="text-[10px] font-semibold text-fg-secondary">
          {readiness.liveReady ? 'Live ready' : 'Live needs work'}
        </span>
      </div>

      <div className="space-y-2">
        <div className="rounded-xl bg-canvas px-4 py-3">
          <div className="flex items-center gap-2">
            <Newspaper size={13} aria-hidden="true" className="text-success-fg" />
            <p className="text-sm font-semibold text-fg">Live page</p>
          </div>
          <p className="mt-1 text-[11px] text-fg-secondary">{pageStatus(livePage, liveLoading, 'Live')}</p>
        </div>

        <div className="rounded-xl bg-canvas px-4 py-3">
          <div className="flex items-center gap-2">
            <Newspaper size={13} aria-hidden="true" className="text-info-fg" />
            <p className="text-sm font-semibold text-fg">Next scheduled</p>
          </div>
          <p className="mt-1 text-[11px] text-fg-secondary">{pageStatus(scheduledPage, scheduledLoading, 'Scheduled')}</p>
        </div>
      </div>

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {readiness.liveReady && readiness.scheduledReady
          ? 'The front page pipeline is populated and has a live issue plus a follow-up issue ready to go.'
          : 'Use this card to see whether the current front page and next scheduled page are both staged.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <CalendarCheck2 size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Front Page Readiness</span>
      </div>
      {body}
    </section>
  )
}
